import React from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  HashtagIcon,
  IdentificationIcon,
  ListBulletIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { PAGE_TYPES } from '@dashboard/enums/page-types';

const elementFieldTypeEnums = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  SELECT: 'select',
  CHECKBOX: 'checkbox',
  MULTIPLE_CHOICE: 'multiple-choice',
  QUANTITY: 'quantity',
  EMAIL: 'email',
  PHONE: 'phone',
  NUMBER: 'number',
  COUNTRY: 'country',
  DATE_TIME: 'date-time',
  DATE: 'date',
  TIME: 'time',
  TAX_ID: 'tax-id',
  PO_NUMBER: 'po-number',
  // tel: 'tel',
  // url: 'url',
  // password: 'password',
  // month: 'month',
  // week: 'week',
  // file: 'file',
  // hidden: 'hidden',
  // price: 'price',
  // reference: 'reference',
};

const elementFieldTypes = [
  {
    name: elementFieldTypeEnums.TEXT,
    label: 'Single line text',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        height="16"
        width="16"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
        />
      </svg>
    ),
  },
  {
    name: elementFieldTypeEnums.TEXTAREA,
    label: 'Multi line text',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        height="16"
        width="16"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    name: elementFieldTypeEnums.NUMBER,
    label: 'Number',
    icon: <HashtagIcon className="w-4 h-4 stroke-2" />,
  },
  {
    name: elementFieldTypeEnums.SELECT,
    label: 'Dropdown select',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        height="16"
        width="16"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 9l4-4 4 4m0 6l-4 4-4-4"
        />
      </svg>
    ),
  },
  {
    name: elementFieldTypeEnums.CHECKBOX,
    label: 'Checkbox',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        height="16"
        width="16"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    name: elementFieldTypeEnums.MULTIPLE_CHOICE,
    label: 'Multiple choice',
    icon: <ListBulletIcon className="w-4 h-4 stroke-2" />,
  },
  {
    name: elementFieldTypeEnums.QUANTITY,
    label: 'Quantity',
    defaultLabel: 'Quantity',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        height="16"
        width="16"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
        />
      </svg>
    ),
  },
  {
    name: elementFieldTypeEnums.EMAIL,
    label: 'Email',
    defaultLabel: 'Email address',
    defaultPlaceholder: 'Email address',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        height="16"
        width="16"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    name: elementFieldTypeEnums.PHONE,
    label: 'Phone',
    defaultLabel: 'Phone',
    defaultPlaceholder: 'Phone',
    icon: <PhoneIcon className="w-4 h-4" />,
  },
  {
    name: elementFieldTypeEnums.COUNTRY,
    label: 'Country',
    defaultLabel: 'Country',
    defaultPlaceholder: 'Country',
    icon: <MapPinIcon className="w-4 h-4" />,
  },
  {
    name: elementFieldTypeEnums.DATE_TIME,
    label: 'Date & time',
    defaultLabel: 'Date & time',
    defaultPlaceholder: 'Date & time',
    icon: <CalendarDaysIcon className="w-4 h-4 stroke-2" />,
  },
  {
    name: elementFieldTypeEnums.DATE,
    label: 'Date',
    defaultLabel: 'Date',
    defaultPlaceholder: 'Date',
    icon: <CalendarDaysIcon className="w-4 h-4 stroke-2" />,
  },
  {
    name: elementFieldTypeEnums.TIME,
    label: 'Time',
    defaultLabel: 'Time',
    defaultPlaceholder: 'Time',
    icon: <ClockIcon className="w-4 h-4 stroke-2" />,
  },
  {
    name: elementFieldTypeEnums.TAX_ID,
    label: 'Tax ID',
    defaultLabel: 'Tax ID',
    defaultPlaceholder: 'Tax ID',
    icon: <IdentificationIcon className="w-4 h-4 stroke-2" />,
  },
  {
    name: elementFieldTypeEnums.PO_NUMBER,
    label: 'PO Number',
    defaultLabel: 'PO Number',
    defaultPlaceholder: 'PO Number',
    icon: <DocumentTextIcon className="w-4 h-4 stroke-2" />,
  },
];

const getElementFieldTypes = pageType => {
  if (pageType === PAGE_TYPES.EVENT) {
    return Object.values(elementFieldTypeEnums).filter(
      field => field !== elementFieldTypeEnums.QUANTITY,
    );
  }

  return Object.values(elementFieldTypeEnums);
};

const getElementFieldTypeLabel = name => {
  const fieldType = elementFieldTypes.find(type => type.name === name);

  return fieldType ? fieldType.label : '';
};

const getElementFieldTypeIcon = name => {
  const fieldType = elementFieldTypes.find(type => type.name === name);

  return fieldType ? fieldType.icon : '';
};

const getElement = name => {
  const fieldType = elementFieldTypes.find(type => type.name === name);

  return fieldType;
};

export {
  elementFieldTypeEnums,
  elementFieldTypes,
  getElement,
  getElementFieldTypes,
  getElementFieldTypeIcon,
  getElementFieldTypeLabel,
};
